import PropTypes from 'prop-types';
import { Image as PdfImage, Text, View } from '@react-pdf/renderer';

import PdfFindingCard from './PdfFindingCard';
import { CATEGORY_COLORS, COLORS, styles } from './styles';

// A4 content width: 595 - 40*2 margins = 515pt
const CONTENT_WIDTH = 515;
// Max image height to prevent a single image from taking the whole page
const MAX_IMAGE_HEIGHT = 380;

const PdfScreenSection = ({ pdfScreen }) => {
  const { name, pageNum, totalPages, imageBase64, imageWidth, imageHeight, findings } = pdfScreen;

  // Calculate image display dimensions
  let displayWidth = CONTENT_WIDTH;
  let displayHeight = imageWidth > 0
    ? (CONTENT_WIDTH * imageHeight) / imageWidth
    : 300;

  // Cap the image height
  if (displayHeight > MAX_IMAGE_HEIGHT) {
    displayHeight = MAX_IMAGE_HEIGHT;
    displayWidth = imageWidth > 0
      ? (MAX_IMAGE_HEIGHT * imageWidth) / imageHeight
      : CONTENT_WIDTH;
  }

  return (
    <View>
      {/* Screen header */}
      <View style={styles.screenHeader} fixed>
        <View>
          <Text style={styles.screenName}>
            {name}
          </Text>
          <Text
            style={{ fontSize: 9, color: COLORS.textTertiary, marginTop: 2 }}
            render={({ subPageNumber }) => subPageNumber > 1 ? 'Feedback continued' : ''}
          />
        </View>
        <Text style={styles.screenPageNum}>
          {pageNum}
          {' of '}
          {totalPages}
        </Text>
      </View>

      {/* Screen image with pins */}
      {imageBase64 && (
        <View
          style={[styles.imageContainer, { width: displayWidth, height: displayHeight }]}
          wrap={false}
        >
          <PdfImage
            src={imageBase64}
            style={{ width: displayWidth, height: displayHeight, objectFit: 'contain' }}
          />
          {/* Render pins */}
          {findings.map(finding => {
            if (!finding.pinLocation) {
              return null;
            }

            const pinColor = CATEGORY_COLORS[finding.category] || COLORS.warning;

            return (
              <View
                key={finding.number}
                style={[
                  styles.pinOverlay,
                  {
                    backgroundColor: pinColor,
                    left: (finding.pinLocation.x_percent / 100) * displayWidth - 12,
                    top: (finding.pinLocation.y_percent / 100) * displayHeight - 9,
                  },
                ]}
              >
                <Text style={styles.pinText}>
                  {'# '}
                  {finding.number}
                </Text>
              </View>
            );
          })}
        </View>
      )}

      {/* Findings grid - 2 columns */}
      {findings.length > 0 && (
        <View style={styles.findingsGrid}>
          {findings.map(finding => (
            <View style={styles.findingCardWrapper} key={finding.number}>
              <PdfFindingCard finding={finding} />
            </View>
          ))}
        </View>
      )}

      {findings.length === 0 && (
        <View style={{ padding: 20, alignItems: 'center' }}>
          <Text style={{ fontSize: 9, color: COLORS.textTertiary }}>
            No findings for this screen
          </Text>
        </View>
      )}
    </View>
  );
};

PdfScreenSection.propTypes = {
  pdfScreen: PropTypes.shape({
    name: PropTypes.string,
    pageNum: PropTypes.number,
    totalPages: PropTypes.number,
    imageBase64: PropTypes.string,
    imageWidth: PropTypes.number,
    imageHeight: PropTypes.number,
    findings: PropTypes.arrayOf(PropTypes.shape({
      number: PropTypes.number,
      category: PropTypes.string,
      pinLocation: PropTypes.shape({
        x_percent: PropTypes.number,
        y_percent: PropTypes.number,
      }),
    })).isRequired,
  }).isRequired,
};

export default PdfScreenSection;
